import { Monitor, Moon, Sun } from "lucide-react";

import { Hint } from "@/design-system/Hint.tsx";
import { nextThemeMode, THEME_LABELS, type ResolvedTheme, type ThemeMode } from "@/theme/theme.ts";

const ICONS = { system: Monitor, light: Sun, dark: Moon } as const;

/**
 * One button cycling system → light → dark. The icon shows the *preference*,
 * not the resolved key, so "system" stays visibly distinct from whichever key
 * it happens to be resolving to right now.
 */
export function ThemeToggle({
  mode,
  resolved,
  onCycle,
}: {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  onCycle: () => void;
}) {
  const Icon = ICONS[mode];
  const next = nextThemeMode(mode);

  return (
    <Hint
      label={THEME_LABELS[mode]}
      detail={
        mode === "system"
          ? `Following your OS (currently ${resolved}). Tap for ${next}.`
          : `Tap for ${next === "system" ? "system" : `${next} theme`}.`
      }
      side="bottom"
    >
      <button
        type="button"
        onClick={onCycle}
        aria-label={`${THEME_LABELS[mode]}. Switch to ${next}.`}
        className="grid size-8 shrink-0 place-items-center rounded-lg border border-rule text-chalk-dim transition hover:border-rule-bright hover:text-chalk active:scale-95"
      >
        <Icon className="size-3.5" />
      </button>
    </Hint>
  );
}
